<!--

============================================================================================================
||    AIFlows page                                                                                        ||
============================================================================================================

|   Description
------------------------------------------------------------------------------------------------------------
  Page for managing AI flows.

|   Features
------------------------------------------------------------------------------------------------------------
  - No flows created view
  - List of flows
  - Multiple flow groups
  - Filter flows
  - Create new flow

|   TODO
------------------------------------------------------------------------------------------------------------
  - loading flows
  - scroll sticky header

-->

<template>
  <div
    v-click-outside="abortNewFlow"
    class="flows-wrapper w-100"
    :class="{'announcement-space': showAnnouncement }" >
    <ScrollArea v-if="createFlowForNumber">
      <div
        class="stepper-wrapper sb-margin-x-40">
        <Stepper
          class="stepper"
          :steps="flowCreateSteps"
          finish-btn-text="Create"
          @abort="abortNewFlow"
          @finish="createNewFlow" />
      </div>
    </ScrollArea>
    <InfoCard
      v-else-if="flowsLoaded && Object.keys(flowsList).length === 0"
      :image="require('@/assets/img/no-flow.svg')"
      title="Set up your first AI flow"
      subtitle="Create an AI flow that will assign AI assistants to conversations with customers."
      :background-color1="SBColors.white"
      :background-color2="SBColors.greyUltralight" >
      <template slot="action-slot">
        <b-button
          id="create-flow-btn"
          class="flex center-all"
          @click="startNewFlow" >
          <SBIcon
            class="mr-2"
            :icon="SBIconList.PLUS"
            :color="SBColors.blackPrimary"
            :size="8"/>
          <span>Create</span>
        </b-button>
      </template>
    </InfoCard>
    <ScrollArea
      v-else-if="flowsLoaded && Object.keys(flowsList).length > 0"
      ref="flowsScrollbar" >
      <PageTitle
        image="assets/img/SB-Icons/AI-stars.svg"
        title="Flows"
        class="sb-margin-x-40" />
      <div class="sticky-top">
        <!-- Mask is blocking the lower scrollable layer to become visible above the sticky top -->
        <div class="mask" />
        <PageFilters
          v-model="filterData"
          class="sb-margin-x-40 flow-filters mb-3"
          show-search
          no-debounce
          :filter-numbers="numberOfBusinesses > 1"
          search-text="Search flows..."
          @add-new="startNewFlow" />
      </div>

      <!-- FLOWS list -->
      <div
        v-if="flowsLoaded && filterData.search && !Object.values(flowsList).flat().some((f) => !f.hide)"
        class="no-data flex column center-all">
        <span
          v-if="filterData.search || filterData.numbersFilter.length"
          class="wrapper flex center-all">
          <span class="emoji">😐</span>
          <p>
            We couldn't find anything for
            <strong v-if="filterData.search">{{ filterData.search }}</strong>
            <span v-if="filterData.search && filterData.numbersFilter.length"> and</span>
            <span v-if="filterData.numbersFilter.length"> selected filter{{ filterData.numbersFilter.length > 1 ? 's' : '' }}</span>
          </p>
        </span>
        <span
          v-if="filterData.length"
          class="clear-filters-btn"
          @click="clearFilter" >Clear filter{{ filterData.numbersFilter.length > 1 ? 's' : '' }}</span>
      </div>
      <template v-else>
        <div
          v-for="(number, index) in Object.keys(flowsList)"
          :key="index" >
          <BlockTitle
            v-if="numberOfBusinesses > 1 && !flowsList[number].every(flow => flow.hide)"
            class="sb-margin-x-40"
            :title="getBusinessById(number).name"
            :emoji="getBusinessById(number).emoji" />
          <DraggableList
            v-model="flowsList[number]"
            pointer
            toggle-tooltip="Flow is turned"
            @selected="flowSelected"
            @remove="deleteFlow"
            @reorder="changeOrder"
            @activate="activateFlow" />
        </div>
      </template>
    </ScrollArea>

    <Spinner
      v-if="!flowsLoaded && !activateOrganisation"
      class="mt-5"
      :thickness="2" />
    <b-popover
      ref="branchFilterPopover"
      :target="flowsLoaded && Object.keys(flowsList).length === 0 ? 'create-flow-btn' : 'new-btn'"
      triggers="manual"
      placement="bottom"
      no-fade
      custom-class="interactive-popover organisation-panel-popover hide-arrow"
      @hide="endBranchSelect" >
      <SelectBranch
        v-click-outside="closePopover"
        title="Select phone number"
        only-available
        @branch-selected="branchSelected" />
    </b-popover>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import Spinner from '@/components/common/Spinner';
import NoPermissionMixin from '@/mixins/AccessPermission';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import DraggableList from '@/components/common/DraggableList';
import ScrollArea from '@/components/common/ScrollArea';
import SelectBranch from '@/components/business/branch/SelectBranch';
import ToasterMixin from '@/mixins/Toaster';
import { ModalSize } from '@/helpers/enums/modal.enum';
import PageTitle from '../common/PageTitle';
import PageFilters from '../common/PageFilters';
import BlockTitle from '../common/BlockTitle';
import InfoCard from '../common/InfoCard2';
import Stepper from '../common/Stepper';
import FlowNameStep from '../business/flows/create_steps/FlowName.step';
import FlowTriggerStep from '../business/flows/create_steps/FlowTrigger.step';
import FlowTriggerDetailsStep from '../business/flows/create_steps/FlowTriggerDetails.step';
import FlowTriggerFrequency from '../business/flows/create_steps/FlowTriggerFrequency.step';

export default {
  name: 'AIFlows',
  components: {
    DraggableList,
    Spinner,
    SBIcon,
    PageTitle,
    PageFilters,
    ScrollArea,
    BlockTitle,
    InfoCard,
    SelectBranch,
    Stepper,
  },
  mixins: [
    NoPermissionMixin,
    UserPermissionsMixin,
    ToasterMixin,
  ],
  data() {
    return {
      filterData: {},
      flowsLoaded: false,
      createFlowForNumber: null,
      flowCreateSteps: [
        FlowNameStep,
        FlowTriggerStep,
        FlowTriggerDetailsStep,
        FlowTriggerFrequency,
      ],
      flowsList: {},
      reorderRunning: false,
      preventAbort: false,
      SBColors,
      SBIconList,
      // We must figure out from the list which order number is highest so we can add the new flow to the end.
      // Just counting the flows is not enough because they can be deleted and the order number can be reused.
      lastOrder: 0,
      /** Change in ordering must be done asynchronously so we have to keet track of order changes and run it in a queue. */
      changeOrderQueue: [],
    };
  },
  computed: {
    ...mapGetters({
      currentOrganisation: 'organisation/currentOrganisation',
      permissionDenied: 'user/permissionDenied',
      flows: 'flows/flows',
      flowsMeta: 'flows/flowsMeta',
      flowsDefinitions: 'flows/flowsDefinitions',
      getBusinessById: 'organisation/getBusinessById',
      showAnnouncement: 'system/showAnnouncement',
      organisationFeatures: 'organisation/features',
      me: 'organisation/me',
    }),
    activateOrganisation() {
      return this.currentOrganisation && (this.currentOrganisation.should_activate_account || this.currentOrganisation.should_reactivate_account);
    },
    numberOfBusinesses() {
      return this.currentOrganisation.businesses.length;
    },
  },
  watch: {
    activateOrganisation: {
      handler() {
        if (this.activateOrganisation) {
          // Redirect to contacts so the content below blured activation background isn't empty
          this.$router.push({ name: 'OrgContacts', params: { organisationId: this.currentOrganisation.id } }).catch(() => { });
        }
      },
      immediate: true,
    },
    /**
     * changeOrderQueue watcher will run the changeOrderQueue function if there are items in the queue and the reorderRunning flag is not set.
     * Once the order change is done the reorderRunning flag will be set to false and the first item in the queue will be removed triggering a watcher to run again.
     */
    changeOrderQueue() {
      if (this.changeOrderQueue.length > 0 && !this.reorderRunning) {
        this.reorderRunning = true;
        const firstReorderData = this.changeOrderQueue[0];
        this.updateFlowOrder({ numberId: firstReorderData.item.numberId, flowId: firstReorderData.item.id, order: firstReorderData.newIndex })
          .then((response) => {
            if (response.status !== 200) {
              this.showToaster('Something went wrong. Please try again.', null, null, 'error');
            }
          })
          .catch(() => {
            this.showToaster('Something went wrong. Please try again.', null, null, 'error');
          })
          .finally(() => {
            this.reorderRunning = false;
            this.changeOrderQueue.shift();
          });
      }
    },
    filterData() {
      this.filterFlowList();
    },
    permissionDenied() {
      this.flowsLoaded = true;
    },
    /**
     * flowsMeta watcher will parse the data and set flowsList object by numbers and populate required parameters for the draggable list.
     * It will also load the next page of flows until the last page is reached.
     */
    flowsMeta() {
      // if (this.flowsMeta.current_page < this.flowsMeta.last_page) {
      //   this.getOrganisationFlows({ organisationId: this.currentOrganisation.id, data: { page: this.flowsMeta.current_page + 1 } });
      // } else {
      //   this.proccessFlowList();
      // }
    },
    flows() {
      this.proccessFlowList();
    },
    organisationFeatures() {
      if (!this.organisationFeatures || !this.organisationFeatures.organisation_features) {
        return;
      }
      let orgFlows;
      try {
        orgFlows = this.organisationFeatures.organisation_features.FLOWS.find(f => f.code === 'FLOWS');
      } catch (e) {
        orgFlows = {};
      }
      if (!orgFlows || !orgFlows.feature_active) {
        this.$router.push({ name: 'OrgContacts', params: { organisationId: this.currentOrganisation.id } }).catch(() => { });
      }
    },
  },
  mounted() {
    if (this.emptyObject(this.flowsDefinitions)) {
      this.getFlowsDefinitions();
    }
    this.getOrganisationFlows({ organisationId: this.currentOrganisation.id });
  },
  beforeDestroy() {
    this.clearOrganisationFlows();
  },
  methods: {
    ...mapActions({
      getFlowsDefinitions: 'flows/getFlowsDefinitions',
      getOrganisationFlows: 'flows/getOrganisationFlows',
      createFlow: 'flows/createFlow',
      deleteFlowVuex: 'flows/deleteFlow',
      clearOrganisationFlows: 'flows/clearOrganisationFlows',
      updateFlowOrder: 'flows/updateFlowOrder',
      activateFlowVUEX: 'flows/activateFlow',
    }),
    changeOrder(data) {
      this.changeOrderQueue.push(data);
    },
    proccessFlowList() {
      this.flowsList = {};
      this.flowsLoaded = true;
      this.flows.forEach((flow) => {
        let hide = true;
        if (
          (
            (!this.filterData.search || flow.name.toLowerCase().indexOf(this.filterData.search.toLowerCase()) >= 0) &&
            (!this.filterData.numbersFilter || !this.filterData.numbersFilter.length || this.filterData.numbersFilter.includes(flow.business_uid))
          )
        ) {
          hide = false;
        }
        if (!this.flowsList[flow.business_uid]) {
          this.flowsList[flow.business_uid] = [];
        }
        this.flowsList[flow.business_uid].push({
          id: flow.flow_id,
          numberId: flow.business_uid,
          label: flow.name,
          active: flow.active,
          image: flow.icon ? flow.icon : null,
          hide,
        });
        if (this.lastOrder < flow.order) {
          this.lastOrder = flow.order;
        }
      });
    },
    filterFlowList() {
      Object.keys(this.flowsList).forEach((number) => {
        this.flowsList[number].forEach((flow) => {
          let hide = true;
          if (
            (
              (!this.filterData.search || flow.label.toLowerCase().indexOf(this.filterData.search.toLowerCase()) >= 0) &&
              (!this.filterData.numbersFilter || !this.filterData.numbersFilter.length || this.filterData.numbersFilter.includes(flow.numberId))
            )
          ) {
            hide = false;
          }
          flow.hide = hide;
        });
      });
    },
    abortNewFlow() {
      if (!this.preventAbort) {
        this.createFlowForNumber = null;
      }
    },
    flowSelected(flow) {
      this.$router.push({
        name: 'FlowTree',
        params: {
          flowId: flow.id,
          numberId: flow.numberId,
          flowName: flow.label,
          flowActive: flow.active,
        },
      });
    },
    closePopover() {
      this.$refs.branchFilterPopover.$emit('close');
    },
    startNewFlow() {
      if (this.currentOrganisation.businesses.length === 1) {
        this.branchSelected(this.currentOrganisation.businesses[0]);
      } else if (this.currentOrganisation.businesses.length > 1) {
        this.startBranchSelect();
      } else {
        this.$numberInfoModal.show({
          size: ModalSize.MEDIUM,
          title: 'Add a phone number to create a flow',
          message: 'Please add a phone number to create a flow.',
          confirmButtonText: 'Add a phone number',
          confirmCallback: () => {
            window.open(`https://tally.so/r/wgA5KO?email=${this.me.email}&name=${this.currentOrganisation.name}`, '_blank');
          },
        });
      }
    },
    createNewFlow(data) {
      const frequencyId = data.flowTriggerFrequency.flowTriggerFrequenciesDetails.selected[0].id;
      const frequencyValue = data.flowTriggerFrequency.flowTriggerFrequenciesDetails.selected[0].additionalData ? data.flowTriggerFrequency.flowTriggerFrequenciesDetails.selected[0].additionalData.seconds : null;
      // frequencyObject
      const frequencyObject = {
        params: {
          trigger_frequency: {
            type: frequencyId,
          },
        },
      };
      if (frequencyValue) {
        frequencyObject.params.trigger_frequency.value = frequencyValue;
      }

      // Trigger group
      const flowTriggerGroupId = data.flowTrigger.selected[0].id;

      // Trigger
      const flowTriggerId = data.flowTriggerDetails.flowTriggerDetails.selected[0].id;
      let flowTriggerTags;
      try {
        flowTriggerTags = [...data.flowTriggerDetails.flowTriggerDetails.selected[0].additionalData.tags.map(tag => tag.tag)];
      } catch (error) { /* Do nothing */ }
      let flowTriggerOperator;
      try {
        flowTriggerOperator = data.flowTriggerDetails.flowTriggerDetails.selected[0].additionalData.condition;
      } catch (error) { /* Do nothing */ }

      // Trigger Object
      const triggerObject = {
        blocks: [
          {
            type: flowTriggerGroupId,
            category: 'triggers',
            order: 0,
            params: {
              triggers: [
                {
                  type: flowTriggerId,
                },
              ],
            },
          },
        ],
      };

      if (flowTriggerTags) {
        triggerObject.blocks[0].params.triggers[0].value = flowTriggerId === 'full_match' ? flowTriggerTags[0] : flowTriggerTags;
      }
      if (flowTriggerOperator) {
        triggerObject.blocks[0].params.triggers[0].operator = {
          value: flowTriggerOperator,
        };
      }

      const payload = {
        order: this.lastOrder + 1,
        // trigger name
        name: data.flowName.flowName,
        // trigger
        ...triggerObject,
        ...frequencyObject,
      };

      this.createFlow({ numberId: this.createFlowForNumber, data: payload })
        .then((response) => {
          if (response.status === 200) {
            this.createFlowForNumber = null;
            this.lastOrder++;
            // redirect to the created flow
            this.$router.push({
              name: 'FlowTree',
              params: {
                flowId: response.data.flow_data.flow_id,
                numberId: response.data.flow_data.business_uid,
                flowName: response.data.flow_data.name,
                flowActive: response.data.flow_data.active,
              },
            });
          } else {
            this.showCreateFlowError();
          }
        })
        .catch((error) => {
          this.showCreateFlowError(error);
        });
    },
    showCreateFlowError(error = null) {
      const message = error && error.response && error.response.data && error.response.data.message ? error.response.data.message : 'Something went wrong. Please try again.';
      this.showToaster(message, null, null, 'error');
    },
    deleteFlow(flow) {
      this.$confirmPluginModal.show({
        buttonVariant: 'RED',
        title: 'Delete flow',
        message: `Are you sure you want to delete flow <b>${flow.label}</b>?`,
        confirmButtonText: 'Delete',
        showUndoWarning: false,
        autoClose: 'NO',
        confirmCallback: () => {
          this.deleteFlowVuex({ numberId: flow.numberId, flowId: flow.id })
            .then((response) => {
              if (response.status === 200) {
                this.showToaster('Flow has been successfully deleted.', null, null, 'OK');
              } else {
                this.showToaster('Something went wrong. Please try again.', null, null, 'error');
              }
            })
            .catch((error) => {
              this.showToaster('Something went wrong. Please try again.', null, null, 'error');
            })
            .finally(() => {
              this.$confirmPluginModal.close();
            });
        },
      });
    },
    startBranchSelect() {
      this.$refs.branchFilterPopover.$emit('open');
    },
    endBranchSelect() {},
    branchSelected(branch) {
      this.preventAbort = true;
      if (this.currentOrganisation.businesses.length > 1) {
        this.closePopover();
        this.$refs.branchFilterPopover.$on('hidden', () => { // if we are not waiting for the popover to close then the popover looses its root since the DOM changes and there is a quick glitch in the UI with the popover jumping to the top left corner
          this.createFlowForNumber = branch.id;
        });
      } else {
        this.createFlowForNumber = branch.id;
      }
      setTimeout(() => {
        this.preventAbort = false;
      }, 500);
    },
    activateFlow(item) {
      if (item.active) {
        this.activateFlowVUEX({ numberId: item.numberId, flowId: item.id, active: true });
      } else {
        this.activateFlowVUEX({ numberId: item.numberId, flowId: item.id, active: false });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.flows-wrapper {
  background-color: $white;
  z-index: 998;
}

.stepper-wrapper {
  margin-top: 58px;
  .stepper {
    max-width: 510px;
    margin: 0 auto;
  }
}

.sticky-top {
  background-color: white;
  top: 20px;

  .mask {
    position: absolute;
    top: -20px;
    left: 0;
    right: 0;
    height: 20px;
    background: white;
    z-index: -1;
  }

  .flow-filters {
    padding-top: unset !important;
  }
}

.no-data {
  border-top: none !important;
  margin-left: 100px;
  margin-right: 100px;
}
</style>
